import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LANDING_CONTENT as C } from '../components/landing/landingContent';
import { API_BASE } from '../services/api';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`${API_BASE}/api/blog/${slug}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then((data) => { if (data) setPost(data); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const pageTitle = post.seo_title || post.title;
    const pageDesc = post.meta_description || post.excerpt || '';
    document.title = `${pageTitle} · ${C.brand.name}`;

    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // FOCUS-KEYWORD-SEO-FIX: focus_keyword/tags were captured in the admin
    // form and saved to the DB but never actually rendered anywhere on the
    // public page — a meta keywords tag combining both is a real, if minor,
    // SEO signal search engines still parse, and costs nothing to add.
    const keywords = [post.focus_keyword, post.tags].filter(Boolean).join(', ');
    if (keywords) setMeta('keywords', keywords);

    if (pageDesc) setMeta('description', pageDesc);
    setMeta('og:title', pageTitle, true);
    if (pageDesc) setMeta('og:description', pageDesc, true);
    setMeta('og:type', 'article', true);
    if (post.cover_image_url) setMeta('og:image', post.cover_image_url, true);
  }, [post]);

  return (
    <div className="blpp-page">
      <style>{CSS}</style>
      <header className="blpp-header">
        <Link to="/" className="blpp-brand">{C.brand.name}</Link>
        <Link to="/blog" className="blpp-back">← All Posts</Link>
      </header>

      {loading ? (
        <div className="blpp-loading">Loading...</div>
      ) : notFound || !post ? (
        <div className="blpp-notfound">
          <h1>Post not found</h1>
          <Link to="/blog" className="blpp-notfound-link">Back to blog</Link>
        </div>
      ) : (
        <article className="blpp-article">
          {post.cover_image_url && <img src={post.cover_image_url} alt={post.cover_image_alt || ''} className="blpp-cover" />}
          <div className="blpp-article-body">
            <h1 className="blpp-title">{post.title}</h1>
            {post.published_at && (
              <div className="blpp-date">
                {new Date(post.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            )}
            {post.tags && (
              <div className="blpp-tags">
                {post.tags.split(',').map((t) => t.trim()).filter(Boolean).map((t) => (
                  <span key={t} className="blpp-tag">{t}</span>
                ))}
              </div>
            )}
            <div className="blpp-content" dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </article>
      )}
    </div>
  );
}

const CSS = `
.blpp-page { min-height: 100vh; background: var(--bg-primary, #0b0f1a); color: var(--text-primary, #e2e8f0); }
.blpp-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 32px; border-bottom: 1px solid rgba(255,255,255,0.08); }
.blpp-brand { font-size: 16px; font-weight: 800; color: var(--text-primary, #e2e8f0); text-decoration: none; }
.blpp-back { font-size: 13px; color: var(--text-muted, #94a3b8); text-decoration: none; }
.blpp-back:hover { color: #a5b4fc; }

.blpp-loading, .blpp-notfound { text-align: center; padding: 100px 24px; color: var(--text-muted, #94a3b8); }
.blpp-notfound h1 { font-size: 24px; color: var(--text-primary, #e2e8f0); }
.blpp-notfound-link { color: #a5b4fc; font-size: 14px; }

.blpp-article { max-width: 760px; margin: 0 auto; padding: 40px 24px 100px; }
.blpp-cover { width: 100%; max-height: 400px; object-fit: cover; border-radius: 16px; margin-bottom: 32px; }
.blpp-title { font-size: 34px; font-weight: 800; margin: 0 0 12px; line-height: 1.25; }
.blpp-date { font-size: 13px; color: var(--text-muted, #94a3b8); font-weight: 600; margin-bottom: 32px; }
.blpp-tags { display: flex; flex-wrap: wrap; gap: 8px; margin: -20px 0 32px; }
.blpp-tag { font-size: 12px; font-weight: 600; color: #a5b4fc; background: rgba(99,102,241,0.12); border: 1px solid rgba(99,102,241,0.25); border-radius: 20px; padding: 4px 12px; }
.blpp-content { font-size: 16px; line-height: 1.75; color: var(--text-primary, #e2e8f0); }
.blpp-content p { margin: 0 0 18px; }
.blpp-content h2 { font-size: 24px; font-weight: 700; margin: 32px 0 14px; }
.blpp-content h3 { font-size: 19px; font-weight: 700; margin: 26px 0 12px; }
.blpp-content ul, .blpp-content ol { margin: 0 0 18px; padding-left: 24px; }
.blpp-content li { margin-bottom: 8px; }
.blpp-content blockquote { border-left: 3px solid #6366f1; padding-left: 16px; margin: 0 0 18px; color: var(--text-muted, #94a3b8); font-style: italic; }
.blpp-content img { max-width: 100%; border-radius: 10px; margin: 12px 0; }
.blpp-content a { color: #a5b4fc; }
/* TABLE-PASTE-FIX: matches the editor's own table styling (AdminBlogManagement.js)
   so a table looks the same on the live page as it did while writing it —
   without this, the raw <table> HTML renders with the browser's default
   (no visible borders/grid at all). */
.blpp-content table { border-collapse: collapse; width: 100%; margin: 0 0 18px; }
.blpp-content th, .blpp-content td { border: 1px solid rgba(255,255,255,0.18); padding: 10px 12px; text-align: left; }
.blpp-content th { background: rgba(255,255,255,0.06); font-weight: 600; }
`;
